import { Fragment, isValidElement, type ReactNode } from 'react';

import { cn } from '@nesso/ui/lib/utils';

const orientationVariants = {
    horizontal: { list: 'flex-row items-center', separator: '' },
    responsive: {
        list: 'flex-col items-start max-sm:gap-1 sm:flex-row sm:items-center',
        separator: 'max-sm:hidden',
    },
} as const;

const sizeVariants = {
    default: 'text-base',
    sm: 'text-sm',
} as const;

function isVisible(item: ReactNode) {
    if (typeof item === 'string') return item.trim() !== '';
    return item != null && typeof item !== 'boolean';
}

type Props = {
    items: ReactNode[];
    className?: string;
    orientation?: keyof typeof orientationVariants;
    size?: keyof typeof sizeVariants;
};

export function DotSeparatedText({
    items,
    className,
    orientation = 'horizontal',
    size = 'default',
}: Props) {
    const visibleItems = items.filter(isVisible);

    if (visibleItems.length === 0) return null;

    const { list, separator } = orientationVariants[orientation];

    return (
        <p className={cn('flex gap-2 text-muted-foreground', sizeVariants[size], list, className)}>
            {visibleItems.map((item, index) => (
                // Items can repeat, so the value is no key; an element's own key keeps its state if siblings hide.
                <Fragment key={isValidElement(item) && item.key != null ? item.key : `i-${index}`}>
                    {index > 0 && (
                        <span aria-hidden="true" className={separator || undefined}>
                            ·
                        </span>
                    )}
                    <span className="min-w-0 max-w-75 wrap-anywhere">{item}</span>
                </Fragment>
            ))}
        </p>
    );
}
